import type { Listing } from '../types/housing'
import { candidates } from '../data/roommates'
import { defaultPreferences } from '../data/roommates'
import { rankCandidates } from './compatibility'

/**
 * The Campuzon difference on a property card: a home is only half the answer.
 *
 * This derives the roommate signal from the SAME compatibility engine the
 * roommate surface uses — it is not a decorative number. Candidates are
 * attached to a listing by matching what they are actually looking for
 * (kind and budget), so a listing with nothing compatible honestly shows
 * nothing rather than an invented figure.
 */
export interface RoommateSignal {
  count: number
  averageScore: number
  initials: string[]
}

export function roommateSignal(listing: Listing): RoommateSignal | null {
  const ranked = rankCandidates(defaultPreferences, candidates).filter((match) => {
    const wants = match.candidate.preferences
    const kindFits = wants.kind === listing.kind
    const budgetFits = listing.rentPerYear <= wants.budgetMax * 1.1
    return kindFits && budgetFits
  })

  if (ranked.length === 0) return null

  const shown = ranked.slice(0, 3)
  return {
    count: ranked.length,
    averageScore: Math.round(
      shown.reduce((sum, match) => sum + match.score, 0) / shown.length,
    ),
    initials: shown.map((match) => match.candidate.initials),
  }
}

/** Straight-line distance is what we can honestly state; walking time is not. */
export function distanceLabel(listing: Listing): string {
  const km = Math.max(0.3, Math.round((listing.minutesToCampus / 12) * 10) / 10)
  return `${km} km from campus`
}

export function priceMarkerLabel(rent: number): string {
  return `₦${Math.round(rent / 1000)}k`
}
